import Upgrade from '../Upgrade';
import cloneDeep from 'lodash/cloneDeep';
import ActionText from '../../core/ActionText';
import ProjectContainer from '../../core/project/ProjectContainer';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import AssignmentIcon from '@material-ui/icons/Assignment';
import "@triply/yasgui/build/yasgui.min.css";
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import GS from '../../grafsync/grafsync';
import Errored from '../Errored';
import MenuSelector from '../MenuSelector';
import { globalStyles as styles } from '../style/gstheme';
import "../style/sparql-editor.css";
import { NS_GS } from '../utils/constants';
import { makeCancelable, sortObjects } from '../utils/utils';
import QueryDetail from './QueryDetail';
import { Typography } from '@material-ui/core';

class QueryManager extends Component {

  constructor(props) {
    super(props);
    this.state = {
      selectQueryIndex: null,
      selectedQuery: props.initialQuery || null,
      queries: [],
      allowSavedQueries: GS.getActiveSubscriptionType() !== "Individual" && GS.getActiveSubscriptionType() !== "Standard",
      recentlyDeleted: []
    };
    this.cancellable = null;
  }

  componentDidMount = () => {
    this.state.allowSavedQueries && this.getSavedQueries();
  };

  monitorDetailUpdate = (id) => {
    let newQueries = cloneDeep(this.state.savedQueries);
    if (!newQueries) return;
    if (newQueries.length === 0) return;
    const data = GS.data(id, this.props.graph);
    const updatedQuery = newQueries.find(query => query.id === id);
    if (!updatedQuery) return;
    updatedQuery.title = data.title;
    updatedQuery.isReport = data.isReport;
    updatedQuery.query = data.query;
    this.setState({ savedQueries: newQueries });
  };

  getSavedQueries = async () => {
    try {
      const data = await this.doSearch({
        search: [{
          predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
          value: NS_GS + "SavedQuery"
        }],
        graph: [this.props.graph],
        page: 1,
        noCount: true,
        pageSize: 200
      }).promise;
      if (this.cancellable && this.cancellable.isCancelled()) return;
      const savedQueries = data.results || null;
      sortObjects(savedQueries, "title", this.props.defaultLang, null, false);
      this.setState({ savedQueries });
    }
    catch (e) {
      if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
    }
  };

  doSearch(payload) {
    this.cancellable = makeCancelable(GS.search(payload));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  revertQuery = (query, additionalProjects) => this.props.setQuery(query, additionalProjects);

  selectQuery = (queryIndex) => {
    const queryInfo = this.state.savedQueries[queryIndex];
    this.setState({ selectQueryIndex: queryIndex, selectedQuery: queryInfo.id });
    this.props.setQuery(queryInfo.query, queryInfo.queryProject);
    this.getSavedQueries();
  };

  deleteQuery = (iri) => {
    GS.deleteResource(iri, this.props.graph);
    let updatedRecentlyDeleted = [...this.state.recentlyDeleted];
    updatedRecentlyDeleted.push(iri);
    this.setState({ selectedQuery: null, recentlyDeleted: updatedRecentlyDeleted });
  };

  render() {

    const { graph, isSelected, disabled, defaultLang, isReadOnly, reportOption, queryType, classes } = this.props;
    const { isErrored, savedQueries, selectedQuery, project, recentlyDeleted, allowSavedQueries } = this.state;

    if (isErrored) return <Errored data={isErrored} />;

    const menuOptions = allowSavedQueries && savedQueries && savedQueries.length > 0 ?
      savedQueries
        .filter(sq => !recentlyDeleted.includes(sq.id))
        .map((queryInfo, index) => ({
          label: <span data-test={queryInfo.title[defaultLang]}
            style={{ width: '100%' }}>{queryInfo.title[defaultLang]} <span style={{ float: 'right', marginLeft: 6, marginRight: 6 }}>{
              queryInfo.isReport ?
                <Tooltip data-test={"available-for-report-" + index} title="Query is available for reporting">
                  <AssignmentIcon style={{ verticalAlign: 'middle' }} fontSize="small" />
                </Tooltip>
                :
                <span></span>
            }</span></span>,
          value: savedQueries.findIndex(sq => queryInfo.id === sq.id)
        }))
      :
      [];

    return (
      <div style={{ paddingLeft: 12, paddingRight: 12, paddingBottom: 12 }}>
        <Typography className={classes.subHeading} style={{ paddingTop: 4 }}>Saved Queries</Typography>
        {allowSavedQueries ?
          <div>
            {selectedQuery || (menuOptions && menuOptions.length > 0) ?
              <div style={{ marginTop: 12 }}>
                {menuOptions && menuOptions.length > 0 &&
                  <MenuSelector
                    key={selectedQuery}
                    dataTest="query-selector"
                    handleChange={this.selectQuery}
                    hintWord="query"
                    options={menuOptions} />
                }
                <div style={{ marginTop: menuOptions && menuOptions.length > 0 ? 12 : 0 }}>
                  {selectedQuery &&
                    <ProjectContainer {...this.props}
                      component={QueryDetail}
                      isReadOnly={isReadOnly || disabled}
                      key={selectedQuery}
                      queryType={queryType}
                      id={selectedQuery}
                      onDeleteQuery={this.deleteQuery}
                      reportOption={reportOption}
                      disabled={disabled}
                      defaultLang={defaultLang}
                      isNotDirty={isSelected}
                      onRevertQuery={this.revertQuery}
                      updateEvent={this.monitorDetailUpdate}
                      graph={graph}
                      project={project} />
                  }
                </div>
              </div>
              :
              <div style={{ height: 100 }}>
                <ActionText offset="100px">No saved queries.</ActionText>
              </div>
            }
          </div>
          :
          <div data-test="no-access" style={{ paddingTop: 60, width: '100%', textAlign: 'center' }}>
            <Typography align="center" variant="body2">This feature requires a minimum of an Enterprise subscription.</Typography>
            <Upgrade />
          </div>
        }
      </div>
    );
  }
}

QueryManager.propTypes = {
  classes: PropTypes.object,
  isSelected: PropTypes.bool,
  initialQuery: PropTypes.string,
  isReadOnly: PropTypes.bool,
  reportOption: PropTypes.bool,
  queryType: PropTypes.string,
  setQuery: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  defaultLang: PropTypes.string.isRequired,
  graph: PropTypes.string.isRequired
};

export default withStyles(styles)(QueryManager);